import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import HomeCliente from './pages/HomeCliente';
import PerfilCliente from './pages/PerfilCliente';
import TrendPage from './pages/TrendPage';
import CurriculoGerado from './components/CurriculoGerado';
import apiClient from './api';
import ConfirmacaoPage from './components/ConfirmacaoPage';
import HomePage from './pages/HomePage'; 
import { ThemeProvider } from 'styled-components';
import { GlobalStyle } from './styles/GlobalStyle';
import { theme } from './theme';
import RequestDemoPage from './pages/RequestDemoPage';
import SocialConnectPage from './pages/SocialConnectPage';
import InstagramCallbackPage from './pages/InstagramCallbackPage';
import AdminInvitePage from './pages/AdminInvitePage';
import CompletarCadastroPage from './pages/CompletarCadastroPage';
import RecuperarSenhaPage from './pages/RecuperarSenhaPage';
import ResetarSenhaPage from './pages/ResetarSenhaPage';
import LegalPage from './pages/LegalPage'; 

import LoadingScreen from './components/common/LoadingScreen';

function AppRoutes({ user, setUser, isLoadingAuth, perfil, curriculo, setCurriculo, isLoadingData  }) {
  const navigate = useNavigate();

  // Funções de login/logout
  const handleLogin = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData)); // Armazena dados para UI
    setUser(userData);
    navigate('/home');
  };

  const handleLogout = useCallback(async () => { 
    try {
      await apiClient.post('/api/logout', {}); // Chama o backend para limpar cookies
      console.log("Logout no backend realizado.");
    } catch (error) {
      console.error("Erro ao fazer logout no backend:", error.response?.data?.msg || error.message);
    } finally {
      localStorage.removeItem('user');
      setUser(null);
      navigate('/');
    }
  }, [setUser, navigate]); 

  if (isLoadingAuth || (user && isLoadingData)) {
        return <LoadingScreen message="Carregando..." />;
  }

  return (
    <Routes>
      // No App.js
      <Route path="/" element={<HomePage user={user} />} />
      <Route path="/termos-de-uso" element={<LegalPage />} />
      <Route path="/politica-de-privacidade" element={<LegalPage />} />
      <Route
        path="/login"
        element={
          user
          ? <Navigate to="/home" replace />
          : <LoginPage onLogin={handleLogin} />
        }
      />
      <Route
        path="/recuperar-senha"
        element={
          user
          ? <Navigate to="/home" replace />
          : <RecuperarSenhaPage />
        }
      />
      <Route
        path="/resetar-senha/:token"
        element={<ResetarSenhaPage />} 
      />
      <Route 
        path="/completar-cadastro/:token" 
        element={
          user 
          ? <Navigate to="/home" replace /> 
          : <CompletarCadastroPage />
        } 
      />
      <Route path="/confirmacao/:status" element={<ConfirmacaoPage />} />
      <Route path="/solicitar-demonstracao" element={<RequestDemoPage />} />
      <Route
        path="/home"
        element={user ? (
          <HomeCliente user={user} perfil={perfil} onLogout={handleLogout} resume={curriculo} />
        ) : (
          <Navigate to="/login" replace />
        )}
      />
      <Route
          path="/trends/:context/:id/:metricName"
          element={user ? <TrendPage /> : <Navigate to="/login" />}
      />
      <Route
        path="/perfil"
        element={
          user ? (
                    <PerfilCliente 
                        user={user}
                        perfil={perfil}
                        curriculo={curriculo}
                        setCurriculo={setCurriculo}
                    />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route 
        path="/curriculos/:uuid"
        element={
          user 
            ? <CurriculoGerado user={user} onLogout={handleLogout} perfil={perfil} resume={curriculo} /> 
            : <Navigate to="/login" replace />
        } 
      />
      <Route
        path="/admin/convidar"
        element={
          user && user.role === 'admin' ? (
            <AdminInvitePage />
          ) : (
            <Navigate to={user ? "/home" : "/login"} replace />
          )
        }
      />
      <Route
        path="/social-connect"
        element={ user ? <SocialConnectPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/instagram-callback"
        element={ user ? <InstagramCallbackPage /> : <Navigate to="/login" replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [perfil, setPerfil] = useState(null);
  const [curriculo, setCurriculo] = useState({
      sites: [],
      academic_experience: [],
      professional_experience: [],
      languages: []
    });
  const [isLoadingData, setIsLoadingData] = useState(false);

  useEffect(() => {
    const checkUserSession = async () => {

      try {
        const response = await apiClient.get('/api/protegido');
        const userDataFromToken = response.data.logged_in_as;

        setUser(userDataFromToken);
        localStorage.setItem('user', JSON.stringify(userDataFromToken));
        
      } catch (error) {
        console.log("Nenhuma sessão ativa encontrada no backend ou token expirado.");
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setIsLoadingAuth(false);
      }
    };

    checkUserSession();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.cliente_id) {
        setIsLoadingData(true);
        try {
          const resPerfil = await apiClient.get(`/api/cliente/${user.cliente_id}`);
          setPerfil(resPerfil.data);
        } catch (error) {
          console.error("Erro ao buscar dados do perfil no App.js", error);
          setPerfil(null);
        } finally {
          setIsLoadingData(false);
        }
      } else {
        setPerfil(null);
      }
    };

    fetchUserData();
  }, [user]);

   // Efeito para lidar com logout global caso o refresh token falhe no interceptor
   useEffect(() => {
     const handleAuthErrorLogout = () => {
       console.log("Evento auth-error-logout recebido. Deslogando...");
       localStorage.removeItem('user');
       setUser(null);
     };
     window.addEventListener('auth-error-logout', handleAuthErrorLogout);
     return () => {
       window.removeEventListener('auth-error-logout', handleAuthErrorLogout);
     };
   }, []); 

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Router>
        <AppRoutes
          user={user}
          setUser={setUser}
          isLoadingAuth={isLoadingAuth}
          perfil={perfil}
          curriculo={curriculo}
          setCurriculo={setCurriculo}
          isLoadingData={isLoadingData}
        />
      </Router>
    </ThemeProvider>
  );
}

export default App;